import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { House, Spinner, Warning } from '@phosphor-icons/react';

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwörter stimmen nicht überein');
            return;
        }

        if (password.length < 6) {
            setError('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }

        setLoading(true);

        const result = await register(email, password, name);
        
        if (result.success) {
            navigate('/dashboard', { replace: true });
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="register-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8 animate-fade-in-up">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <House size={36} weight="fill" className="text-primary" />
                        <span className="font-bold text-2xl tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            OpenImmo Manager
                        </span>
                    </Link>
                </div>

                <Card className="border shadow-none animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            Konto erstellen
                        </CardTitle>
                        <CardDescription>
                            Registrieren Sie sich als Makler
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <Alert variant="destructive" data-testid="register-error">
                                    <Warning size={16} className="inline mr-2" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder="Ihr Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="rounded-sm"
                                    data-testid="name-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="ihre@email.de"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="rounded-sm"
                                    data-testid="email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Mindestens 6 Zeichen"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="rounded-sm"
                                    data-testid="password-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="Passwort wiederholen"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="rounded-sm"
                                    data-testid="confirm-password-input"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full rounded-sm" 
                                disabled={loading}
                                data-testid="register-submit"
                            >
                                {loading ? (
                                    <>
                                        <Spinner className="animate-spin mr-2" size={16} />
                                        Wird registriert...
                                    </>
                                ) : (
                                    'Registrieren'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-muted-foreground">
                            Bereits ein Konto?{' '}
                            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                                Anmelden
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
